<template>
  <v-dialog :model-value="modelValue" max-width="460" @update:model-value="emit('update:modelValue', $event)">
    <v-card v-if="lesson">
      <v-card-title class="d-flex align-center pb-2">
        <v-icon class="me-2" :color="statusColor">mdi-calendar-edit</v-icon>
        <span>{{ t('schedule.edit.title') }}</span>
        <v-spacer />
        <v-chip size="x-small" :color="statusColor" variant="tonal" class="me-2">{{ t(`schedule.status.${lesson.status}`) }}</v-chip>
        <v-btn icon="mdi-close" size="small" variant="text" @click="close" />
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-4">
        <!-- Contact card (iPhone style): name → phone → instructor → call -->
        <div class="ed-contact">
          <div class="ed-name">{{ lesson.studentName || '—' }}</div>
          <div v-if="lesson.phone" class="ed-phone">{{ lesson.phone }}</div>
          <div v-if="lesson.instructorName" class="ed-instr">
            <v-icon size="15">mdi-account-tie</v-icon>
            <span>{{ lesson.instructorName }}</span>
          </div>
          <v-btn
            v-if="lesson.phone"
            block size="large" color="success" variant="flat"
            prepend-icon="mdi-phone" class="ed-call mt-3"
            @click="call(lesson.phone)"
          >{{ t('schedule.edit.call') }}</v-btn>
        </div>

        <v-divider class="my-4" />

        <!-- Date -->
        <div class="ed-label">{{ t('schedule.edit.date') }}</div>
        <input class="ed-input" type="date" v-model="form.date" />

        <!-- Time start/end -->
        <div class="ed-label mt-3">{{ t('schedule.edit.time') }}</div>
        <div class="d-flex align-center ga-2">
          <input class="ed-input ed-time" type="time" v-model="form.start" />
          <span class="ed-sep">—</span>
          <input class="ed-input ed-time" type="time" v-model="form.end" />
        </div>

        <!-- Address — tap-friendly list with add / edit -->
        <div class="ed-label mt-3 d-flex align-center">
          <span>{{ t('schedule.edit.address') }}</span>
          <v-spacer />
          <v-btn size="small" variant="text" color="primary" prepend-icon="mdi-plus" @click="startAdd">
            {{ t('schedule.edit.addAddress') }}
          </v-btn>
        </div>

        <div class="addr-list">
          <div v-for="a in addresses" :key="a.id" class="addr-row" :class="{ on: form.addressId === a.id }">
            <!-- Edit mode -->
            <template v-if="editingId === a.id">
              <input class="ed-input addr-edit-input" v-model="editText" @keyup.enter="saveEdit(a)" />
              <v-btn icon="mdi-check" size="small" variant="text" color="success" :loading="addrBusy" @click="saveEdit(a)" />
              <v-btn icon="mdi-close" size="small" variant="text" @click="editingId = null" />
            </template>
            <!-- View / select mode -->
            <template v-else>
              <button type="button" class="addr-pick" @click="form.addressId = (form.addressId === a.id ? null : a.id)">
                <v-icon size="20" :color="form.addressId === a.id ? 'primary' : 'grey'">
                  {{ form.addressId === a.id ? 'mdi-check-circle' : 'mdi-map-marker' }}
                </v-icon>
                <span>{{ a.address }}</span>
              </button>
              <v-btn icon="mdi-pencil" size="small" variant="text" color="grey" @click.stop="startEdit(a)" />
            </template>
          </div>

          <!-- Add form -->
          <div v-if="adding" class="addr-add">
            <input
              ref="addInputEl"
              class="ed-input addr-edit-input"
              v-model="newText"
              :placeholder="t('schedule.edit.addressPlaceholder')"
              @keyup.enter="saveAdd"
            />
            <v-btn icon="mdi-check" size="small" variant="flat" color="primary" :loading="addrBusy" :disabled="!newText.trim()" @click="saveAdd" />
            <v-btn icon="mdi-close" size="small" variant="text" @click="adding = false" />
          </div>

          <div v-if="!addresses.length && !adding" class="text-caption text-medium-emphasis py-2 px-1">
            {{ t('schedule.edit.noAddress') }}
          </div>
        </div>

        <!-- Note -->
        <div class="ed-label mt-3">{{ t('schedule.edit.note') }}</div>
        <textarea class="ed-input ed-note" v-model="form.note" :placeholder="t('schedule.edit.notePlaceholder')" />
      </v-card-text>

      <v-divider />

      <!-- Delete confirmation bar -->
      <div v-if="confirmDelete" class="del-confirm pa-3">
        <span class="text-error text-body-2 me-auto">
          <v-icon size="18" color="error" class="me-1">mdi-alert-circle</v-icon>
          {{ t('schedule.edit.deleteConfirm') }}
        </span>
        <v-btn variant="text" size="small" :disabled="busy" @click="confirmDelete = false">{{ t('schedule.edit.no') }}</v-btn>
        <v-btn color="error" variant="flat" size="small" :loading="busy" @click="emit('cancel-lesson')">{{ t('schedule.edit.yesDelete') }}</v-btn>
      </div>

      <div v-else class="ed-foot">
        <!-- Complete / Reopen (full width, prominent) -->
        <v-btn
          v-if="lesson.status === 'SCHEDULED'"
          block size="large" color="success" variant="flat" :loading="busy"
          @click="emit('toggle-complete')"
        >
          <v-icon start>mdi-check-circle</v-icon>{{ t('schedule.edit.complete') }}
        </v-btn>
        <v-btn
          v-else-if="lesson.status === 'COMPLETED'"
          block size="large" color="primary" variant="tonal" :loading="busy"
          @click="emit('toggle-complete')"
        >
          <v-icon start>mdi-undo</v-icon>{{ t('schedule.edit.reopen') }}
        </v-btn>

        <!-- Delete + Save row -->
        <div class="d-flex ga-2">
          <v-btn
            v-if="lesson.status !== 'CANCELLED'"
            size="large" color="error" variant="tonal" :disabled="busy"
            class="ed-del-btn" @click="confirmDelete = true"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
          <v-btn
            size="large" color="primary" variant="flat" class="flex-grow-1"
            :loading="busy" :disabled="!canSave" @click="save"
          >
            <v-icon start>mdi-content-save</v-icon>{{ t('schedule.edit.save') }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  lesson: { type: Object, default: null },
  addresses: { type: Array, default: () => [] },
  busy: { type: Boolean, default: false }
})
const emit = defineEmits(['update:modelValue', 'save', 'toggle-complete', 'cancel-lesson', 'add-address', 'update-address'])

const { t } = useI18n()

const form = reactive({ date: '', start: '', end: '', addressId: null, note: '' })
const confirmDelete = ref(false)

// Address add/edit state
const adding = ref(false)
const newText = ref('')
const editingId = ref(null)
const editText = ref('')
const addrBusy = ref(false)
const addInputEl = ref(null)

const startAdd = async () => {
  editingId.value = null
  adding.value = true
  newText.value = ''
  await nextTick()
  addInputEl.value?.focus()
}
const startEdit = (a) => {
  adding.value = false
  editingId.value = a.id
  editText.value = a.address || ''
}
const saveAdd = () => {
  const address = newText.value.trim()
  if (!address) return
  addrBusy.value = true
  emit('add-address', { address }, (created) => {
    addrBusy.value = false
    if (created?.id) {
      form.addressId = created.id
      adding.value = false
      newText.value = ''
    }
  })
}
const saveEdit = (a) => {
  const address = editText.value.trim()
  if (!address) return
  addrBusy.value = true
  emit('update-address', { id: a.id, address }, (ok) => {
    addrBusy.value = false
    if (ok) editingId.value = null
  })
}

const cleanPhone = (p) => String(p || '').replace(/[^\d+]/g, '')
const call = (phone) => {
  const num = cleanPhone(phone)
  if (num) window.location.href = `tel:${num}`
}

const statusColor = computed(() => ({
  SCHEDULED: 'primary', COMPLETED: 'success', CANCELLED: 'error', RESCHEDULED: 'warning'
}[props.lesson?.status] || 'grey'))

const addressItems = computed(() =>
  props.addresses.map(a => ({ title: a.address, value: a.id }))
)

const canSave = computed(() => Boolean(form.date && form.start && form.end))

const minsToTime = (m) => {
  const total = ((m % 1440) + 1440) % 1440
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}
const timeToMins = (tm) => { if (!tm) return 0; const [h, m] = tm.split(':').map(Number); return h * 60 + m }

// Auto-fill end when start changes and end empty/invalid
watch(() => form.start, (v) => {
  if (v && (!form.end || timeToMins(form.end) <= timeToMins(v))) {
    form.end = minsToTime(timeToMins(v) + 60)
  }
})

const initForm = () => {
  const l = props.lesson
  confirmDelete.value = false
  adding.value = false
  editingId.value = null
  newText.value = ''
  if (!l) return
  const [s, e] = String(l.timeSlot || '').split('-')
  form.date = l.dateIso || ''
  form.start = s || ''
  form.end = e || (s ? minsToTime(timeToMins(s) + 60) : '')
  form.addressId = l.raw?.addressId ?? l.addressId ?? null
  form.note = l.note || ''
}

watch(() => props.modelValue, (open) => { if (open) initForm() })
watch(() => props.lesson, () => { if (props.modelValue) initForm() })

const close = () => emit('update:modelValue', false)

const save = () => {
  emit('save', {
    date: form.date,
    timeSlot: `${form.start}-${form.end}`,
    addressId: form.addressId,
    note: form.note.trim()
  })
}
</script>

<style scoped>
.ed-label { font-size: .74rem; color: rgba(var(--v-theme-on-surface), 0.6); margin-bottom: 5px; font-weight: 600; }
.ed-input {
  width: 100%;
  background: rgba(var(--v-theme-on-surface), 0.05);
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.16);
  border-radius: 9px;
  color: rgb(var(--v-theme-on-surface));
  padding: 11px 12px;
  font-size: 1rem;
  outline: none;
  font-family: inherit;
}
.ed-input:focus { border-color: rgb(var(--v-theme-primary)); }
.ed-time { text-align: center; font-weight: 600; }
.ed-sep { font-weight: 700; color: rgba(var(--v-theme-on-surface), 0.5); }
.ed-note { min-height: 70px; resize: vertical; }
.ed-input[type="date"]::-webkit-calendar-picker-indicator,
.ed-input[type="time"]::-webkit-calendar-picker-indicator { filter: invert(0.6); cursor: pointer; }
.del-confirm { display: flex; align-items: center; gap: 8px; background: rgba(var(--v-theme-error), 0.06); }

/* Contact card (iPhone-style) */
.ed-contact { text-align: center; padding: 4px 0 2px; }
.ed-name { font-size: 1.35rem; font-weight: 700; line-height: 1.2; }
.ed-phone { font-size: 1rem; color: #85aad6; margin-top: 3px; }
.ed-instr {
  display: inline-flex; align-items: center; gap: 5px; margin-top: 8px;
  font-size: .85rem; color: rgba(var(--v-theme-on-surface), 0.6);
}
.ed-call { font-weight: 700; letter-spacing: .3px; }

/* Footer actions */
.ed-foot { display: flex; flex-direction: column; gap: 10px; padding: 14px 16px 18px; }
.ed-del-btn { min-width: 56px; }

/* Address list — tap friendly */
.addr-list { display: flex; flex-direction: column; gap: 6px; }
.addr-row {
  display: flex; align-items: center; gap: 4px;
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.14);
  border-radius: 10px; padding: 4px 6px 4px 4px;
}
.addr-row.on { border-color: rgb(var(--v-theme-primary)); background: rgba(var(--v-theme-primary), 0.08); }
.addr-pick {
  flex: 1; display: flex; align-items: center; gap: 9px;
  background: transparent; border: none; color: rgb(var(--v-theme-on-surface));
  text-align: left; cursor: pointer; padding: 9px 4px; font-size: .95rem; min-height: 44px;
}
.addr-edit-input { flex: 1; padding: 10px 12px; font-size: .95rem; }
.addr-add { display: flex; align-items: center; gap: 4px; }
</style>
